import { User } from 'firebase/auth'
import {
  OfflineRecording,
  listPending,
  updateRecording,
  getRecording
} from './offlineRecordings'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export interface UploaderContext {
  firebaseUser: User
  orgId: string
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a'
  if (mimeType.includes('webm')) return 'webm'
  if (mimeType.includes('ogg')) return 'ogg'
  if (mimeType.includes('wav')) return 'wav'
  return 'audio'
}

// 進行中アップロードの中断ハンドル（このセッション内のみ有効）
const activeUploads = new Map<string, { abort: AbortController; storageTask?: { cancel: () => boolean } }>()

/** このセッションで実際に転送が進行中か */
export function hasActiveUpload(id: string): boolean {
  return activeUploads.has(id)
}

/** 進行中のアップロードを中断し、ステータスを「アップロード待ち」に戻す */
export async function cancelUpload(id: string): Promise<OfflineRecording | undefined> {
  const handle = activeUploads.get(id)
  if (handle) {
    try { handle.storageTask?.cancel() } catch { /* noop */ }
    try { handle.abort.abort() } catch { /* noop */ }
    activeUploads.delete(id)
  }
  return updateRecording(id, { status: 'pending', error: undefined })
}

/**
 * アプリ再起動などでセッションが切れて 'uploading' のまま取り残された録音を
 * 'pending' に戻す（進行中のものは触らない）。ページ表示時に呼ぶ。
 */
export async function resetStaleUploads(): Promise<void> {
  const { listRecordings } = await import('./offlineRecordings')
  const all = await listRecordings()
  for (const rec of all) {
    if (rec.status === 'uploading' && !activeUploads.has(rec.id)) {
      await updateRecording(rec.id, { status: 'pending', error: undefined })
    }
  }
}

/**
 * 1件の録音をアップロードする:
 * 1. ドキュメント作成（再試行時は既存ドキュメントを再利用） → 2. Storageへアップロード → 3. 文字起こしリクエスト
 */
export async function uploadRecording(
  id: string,
  ctx: UploaderContext,
  onChange?: (rec: OfflineRecording) => void
): Promise<void> {
  const rec = await getRecording(id)
  if (!rec || rec.status === 'done' || rec.status === 'uploading') return

  const abort = new AbortController()
  const handle: { abort: AbortController; storageTask?: { cancel: () => boolean } } = { abort }
  activeUploads.set(id, handle)

  const notify = async (patch: Partial<Omit<OfflineRecording, 'id'>>) => {
    const next = await updateRecording(id, patch)
    if (next && onChange) onChange(next)
  }

  await notify({ status: 'uploading', error: undefined })

  try {
    const { firebaseUser, orgId } = ctx
    const token = await firebaseUser.getIdToken()

    // 1. ドキュメント作成（過去の試行でdocumentIdが確保済みなら再利用して重複作成を防ぐ）
    let documentId = rec.documentId
    if (!documentId) {
      const createRes = await fetch(`${API_BASE}/v1/organizations/${orgId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: rec.title, category: 'meeting' }),
        signal: abort.signal
      })
      if (!createRes.ok) {
        throw new Error(`ドキュメント作成に失敗しました (${createRes.status})`)
      }
      const created = await createRes.json()
      documentId = typeof created === 'string' ? created : created.id || created.documentId
      if (!documentId) throw new Error('ドキュメントIDを取得できませんでした')
      await notify({ documentId })
    }

    // 2. Firebase Storageへアップロード（キャンセル可能なresumable upload）
    const fileName = `recording_${rec.createdAt}.${extensionFor(rec.mimeType)}`
    const { getStorage, ref, uploadBytesResumable } = await import('firebase/storage')
    const storage = getStorage()
    const storagePath = `audio/${orgId}/${documentId}/${Date.now()}_${fileName}`
    const task = uploadBytesResumable(ref(storage, storagePath), rec.blob, { contentType: rec.mimeType })
    handle.storageTask = task
    await new Promise<void>((resolve, reject) => {
      task.on('state_changed', undefined, reject, () => resolve())
    })
    handle.storageTask = undefined

    // 3. 文字起こしリクエスト（大きいファイルはサーバー側で分割処理され数分かかることがある）
    const freshToken = await firebaseUser.getIdToken()
    const transcribeRes = await fetch(
      `${API_BASE}/v1/organizations/${orgId}/documents/${documentId}/transcribe`,
      {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${freshToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioPath: storagePath, fileName, mimeType: rec.mimeType }),
        signal: abort.signal
      }
    )
    if (!transcribeRes.ok) {
      const text = await transcribeRes.text().catch(() => '')
      // アップロード自体は成功しているのでdocumentIdは保持しつつエラー扱い
      await notify({ status: 'error', documentId, error: `文字起こしリクエストに失敗しました: ${text || transcribeRes.status}` })
      return
    }

    await notify({ status: 'done', documentId, error: undefined })
  } catch (e) {
    if (abort.signal.aborted || (e as any)?.name === 'AbortError' || (e as any)?.code === 'storage/canceled') {
      // キャンセル: cancelUpload側でpendingへ戻している
      return
    }
    const message = e instanceof Error ? e.message : String(e)
    await notify({ status: 'error', error: message })
  } finally {
    activeUploads.delete(id)
  }
}

let syncRunning = false

/**
 * 保留中の録音を順番に(並列度1で)アップロードする。
 * アプリ起動時 / onlineイベント / 録音停止時に呼ばれる。
 */
export async function syncPendingRecordings(
  ctx: UploaderContext,
  onChange?: (rec: OfflineRecording) => void
): Promise<void> {
  if (syncRunning) return
  if (!navigator.onLine) return
  syncRunning = true
  try {
    const pending = await listPending()
    for (const rec of pending) {
      if (!navigator.onLine) break
      await uploadRecording(rec.id, ctx, onChange)
    }
  } finally {
    syncRunning = false
  }
}
